import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Image as ImageIcon } from "lucide-react";

import { API } from "@/lib/api";

export function publicImageUrl(path) {
  if (!path) return null;
  // Derive from the axios baseURL which auto-resolves to the current origin in
  // production (e.g. https://soiree-invites.com/api). This avoids ever pointing
  // at the old build-time REACT_APP_BACKEND_URL when served from a custom domain.
  return `${API}/uploads/file/${path}`;
}

export default function ImageUpload({ value, onChange, label = "Upload image", publicSlug = null, dataTestId = "image-upload" }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Image must be under 8MB"); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", f);
      let endpoint = "/uploads/image";
      if (publicSlug) endpoint = `/uploads/image-public?event_slug=${encodeURIComponent(publicSlug)}`;
      const { data } = await api.post(endpoint, form, { headers: { "Content-Type": "multipart/form-data" } });
      onChange && onChange(data.path);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = () => onChange && onChange(null);

  return (
    <div data-testid={dataTestId}>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={`${dataTestId}-input`} />
      {value ? (
        <div className="relative border border-black/10">
          <img src={publicImageUrl(value)} alt="" className="w-full h-56 object-cover" />
          <button type="button" onClick={remove} data-testid={`${dataTestId}-remove`} className="absolute top-2 right-2 bg-black/70 text-white p-2 rounded-sm hover:bg-black">
            <X size={14} />
          </button>
        </div>
      ) : (
        <button type="button" onClick={onPick} disabled={uploading} data-testid={`${dataTestId}-pick`} className="w-full border border-dashed border-black/20 bg-white p-8 text-center hover:border-neutral-700 transition-colors">
          <div className="flex flex-col items-center gap-2 text-neutral-600">
            {uploading ? <div className="text-sm">Uploading…</div> : (
              <>
                <ImageIcon size={24} />
                <div className="text-sm">{label}</div>
                <div className="text-xs text-neutral-400">JPG, PNG, WebP · Max 8MB</div>
              </>
            )}
          </div>
        </button>
      )}
    </div>
  );
}
